import React, { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

function BuyDownCalculator() {
  // Form state
  const [loanAmount, setLoanAmount] = useState(300000);
  const [baseInterestRate, setBaseInterestRate] = useState(6); // percent per year
  const [buyDownAmount, setBuyDownAmount] = useState(3000);
  const [buyDownInterestRate, setBuyDownInterestRate] = useState(4.5);
  const [buyDownMonths, setBuyDownMonths] = useState(12);
  const [loanTermYears, setLoanTermYears] = useState(30);

  // Helper to calculate monthly payment
  // P = principal, r = monthly interest rate, n = total months
  function calcMonthlyPayment(P, annualRatePercent, n) {
    const r = annualRatePercent / 100 / 12;
    if (r === 0) return P / n;
    return (P * r) / (1 - Math.pow(1 + r, -n));
  }

  // Calculate monthly payments and prepare data for chart and table
  const paymentData = useMemo(() => {
    const totalMonths = loanTermYears * 12;

    // Calculate monthly payments for base and buy-down rates
    const baseMonthlyPayment = calcMonthlyPayment(
      loanAmount,
      baseInterestRate,
      totalMonths
    );

    // Effective loan amount after buy-down amount paid upfront
    // Assume buy-down amount reduces principal directly for simplification (or adjust logic)
    // Alternative: buy-down amount pays for lower interest temporarily, so monthly payment changes only for buyDownMonths

    // Buy-down monthly payment is calculated with buyDownInterestRate for buyDownMonths, then baseInterestRate afterwards

    // For simplicity, assume loan amount unchanged; buy-down affects rate only for buyDownMonths.

    // Monthly payment during buy-down period
    const buyDownMonthlyPaymentDuring = calcMonthlyPayment(
      loanAmount,
      buyDownInterestRate,
      totalMonths
    );

    // After buy-down period, monthly payment at base interest
    const buyDownMonthlyPaymentAfter = baseMonthlyPayment;

    // Prepare array for chart and table
    const data = [];
    for (let month = 1; month <= totalMonths; month++) {
      const payment =
        month <= buyDownMonths
          ? buyDownMonthlyPaymentDuring
          : buyDownMonthlyPaymentAfter;
      data.push({
        month,
        payment: Number(payment.toFixed(2)),
        type: month <= buyDownMonths ? "Buy-Down Rate" : "Base Rate",
        basePayment: Number(baseMonthlyPayment.toFixed(2)),
      });
    }
    return data;
  }, [
    loanAmount,
    baseInterestRate,
    buyDownInterestRate,
    buyDownMonths,
    loanTermYears,
  ]);

  // Calculate total savings during buy-down period
  const totalSavings = useMemo(() => {
    let savings = 0;
    for (let i = 0; i < paymentData.length; i++) {
      if (paymentData[i].month <= buyDownMonths) {
        savings += paymentData[i].basePayment - paymentData[i].payment;
      }
    }
    return savings.toFixed(2);
  }, [paymentData, buyDownMonths]);

  return (
    <div
      style={{
        maxWidth: 1150,
        margin: "auto",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <h1>Mortgage Buy-Down Calculator</h1>

      {/* Input Form */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 20,
          marginBottom: 30,
        }}
      >
        <div style={{ flex: "1 1 200px" }}>
          <label>Loan Amount ($)</label>
          <input
            type="number"
            value={loanAmount}
            onChange={(e) => setLoanAmount(Number(e.target.value))}
            min="0"
            style={{ width: "100%", padding: 8, fontSize: 16 }}
          />
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <label>Base Interest Rate (%)</label>
          <input
            type="number"
            value={baseInterestRate}
            onChange={(e) => setBaseInterestRate(Number(e.target.value))}
            min="0"
            step="0.01"
            style={{ width: "100%", padding: 8, fontSize: 16 }}
          />
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <label>Buy-Down Amount ($)</label>
          <input
            type="number"
            value={buyDownAmount}
            onChange={(e) => setBuyDownAmount(Number(e.target.value))}
            min="0"
            style={{ width: "100%", padding: 8, fontSize: 16 }}
          />
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <label>Buy-Down Interest Rate (%)</label>
          <input
            type="number"
            value={buyDownInterestRate}
            onChange={(e) => setBuyDownInterestRate(Number(e.target.value))}
            min="0"
            step="0.01"
            style={{ width: "100%", padding: 8, fontSize: 16 }}
          />
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <label>Buy-Down Period (months)</label>
          <input
            type="number"
            value={buyDownMonths}
            onChange={(e) => setBuyDownMonths(Number(e.target.value))}
            min="0"
            max={loanTermYears * 12}
            style={{ width: "100%", padding: 8, fontSize: 16 }}
          />
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <label>Loan Term (years)</label>
          <input
            type="number"
            value={loanTermYears}
            onChange={(e) => setLoanTermYears(Number(e.target.value))}
            min="1"
            max="40"
            style={{ width: "100%", padding: 8, fontSize: 16 }}
          />
        </div>
        <div className="RACrightSideBottom00">
          <div>
            <span style={{ color: "red" }}>
              * PMI usually applies when your downpayment or equity in the home
              is under 20%.
            </span>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div style={{ marginBottom: 30 }}>
        <h2>Total Savings During Buy-Down Period: ${totalSavings}</h2>
      </div>

      {/* Chart */}
      <div style={{ width: "100%", height: 300, marginBottom: 40 }}>
        <ResponsiveContainer>
          <LineChart data={paymentData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="month"
              label={{
                value: "Month",
                position: "insideBottomRight",
                offset: -5,
              }}
            />
            <YAxis
              label={{
                value: "Monthly Payment ($)",
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip />
            <Legend verticalAlign="top" height={36} />
            <Line
              type="monotone"
              dataKey="payment"
              stroke="#8884d8"
              name="Buy-Down Rate Payment"
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="basePayment"
              stroke="#82ca9d"
              name="Base Rate Payment"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Payment Table */}
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr>
              <th style={{ borderBottom: "1px solid #ccc", padding: 8 }}>
                Month
              </th>
              <th style={{ borderBottom: "1px solid #ccc", padding: 8 }}>
                Payment (Buy-Down Rate)
              </th>
              <th style={{ borderBottom: "1px solid #ccc", padding: 8 }}>
                Payment (Base Rate)
              </th>
              <th style={{ borderBottom: "1px solid #ccc", padding: 8 }}>
                Period
              </th>
            </tr>
          </thead>
          <tbody>
            {paymentData
              .slice(0, 60)
              .map(({ month, payment, basePayment, type }) => (
                <tr key={month}>
                  <td
                    style={{
                      padding: 8,
                      borderBottom: "1px solid #eee",
                      textAlign: "center",
                    }}
                  >
                    {month}
                  </td>
                  <td
                    style={{
                      padding: 8,
                      borderBottom: "1px solid #eee",
                      textAlign: "right",
                    }}
                  >
                    ${payment.toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: 8,
                      borderBottom: "1px solid #eee",
                      textAlign: "right",
                    }}
                  >
                    ${basePayment.toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: 8,
                      borderBottom: "1px solid #eee",
                      textAlign: "center",
                    }}
                  >
                    {type}
                  </td>
                </tr>
              ))}
            {paymentData.length > 60 && (
              <tr>
                <td
                  colSpan="4"
                  style={{
                    padding: 8,
                    textAlign: "center",
                  }}
                >
                  Showing first 60 months only...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BuyDownCalculator;
